import React from 'react';
import styled from 'styled-components';

const OnboardingContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const OnboardingCard = styled.div`
  background: ${props => props.color ? `rgba(${props.colorRgb}, 0.05)` : 'rgba(255, 35, 0, 0.05)'};
  border: 1px solid ${props => props.color ? `rgba(${props.colorRgb}, 0.3)` : 'rgba(255, 35, 0, 0.3)'};
  border-radius: 12px;
  padding: 16px;
  position: relative;
`;

const OnboardingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`;

const OnboardingIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${props => props.color ? `rgba(${props.colorRgb}, 0.15)` : 'rgba(255, 35, 0, 0.15)'};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 18px;
    height: 18px;
    color: ${props => props.color || '#FF2300'};
  }
`;

const OnboardingTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: white;
`;

const OnboardingDescription = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin-bottom: 16px;
`;

const StepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: ${props => props.completed ? 'rgba(52, 199, 89, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.completed ? 'rgba(52, 199, 89, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.completed ? 'rgba(52, 199, 89, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.completed ? 'rgba(52, 199, 89, 0.4)' : (props.color ? `rgba(${props.colorRgb}, 0.3)` : 'rgba(255, 35, 0, 0.3)')};
  }
`;

const StepNumber = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${props => props.completed ? '#34C759' : (props.color ? `rgba(${props.colorRgb}, 0.2)` : 'rgba(255, 35, 0, 0.2)')};
  border: 1.5px solid ${props => props.completed ? '#34C759' : (props.color || '#FF2300')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.completed ? 'white' : (props.color || '#FF2300')};
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.completed ? 'rgba(255, 255, 255, 0.6)' : 'white'};
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
  margin-bottom: 2px;
`;

const StepDescription = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${props => props.color || '#FF2300'};
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.color ? `linear-gradient(90deg, ${props.color}, #34C759)` : 'linear-gradient(90deg, #FF2300, #34C759)'};
  border-radius: 2px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const DismissButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: rgba(255, 255, 255, 0.7);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Helper function to convert hex color to RGB string
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result 
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '255, 35, 0'; // Default to red
};

/**
 * AppOnboarding - A reusable onboarding component for apps
 * 
 * @param {string} appName - Name of the app
 * @param {string} description - Description of what the app does
 * @param {Array} steps - Array of step objects with { id, title, description, completed, onClick }
 * @param {function} onDismiss - Optional callback to dismiss the onboarding
 * @param {string} actionText - Text for the main action button
 * @param {function} onAction - Callback for the main action button
 * @param {boolean} showProgress - Whether to show progress bar
 * @param {string} color - App theme color (hex format, e.g., '#17A2B8')
 */
const AppOnboarding = ({ 
  appName, 
  description, 
  steps = [], 
  onDismiss,
  actionText,
  onAction,
  showProgress = true,
  icon,
  color = '#FF2300' // Default to DIZEE red
}) => {
  const completedSteps = steps.filter(step => step.completed).length;
  const progress = steps.length > 0 ? (completedSteps / steps.length) * 100 : 0;
  const colorRgb = hexToRgb(color);
  
  // If all steps are completed, don't show onboarding
  if (steps.length > 0 && completedSteps === steps.length) {
    return null;
  }
  
  return (
    <OnboardingContainer>
      <OnboardingCard color={color} colorRgb={colorRgb}>
        {onDismiss && (
          <DismissButton onClick={onDismiss} title="Dismiss">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </DismissButton>
        )}
        
        <OnboardingHeader>
          <OnboardingIcon color={color} colorRgb={colorRgb}>
            {icon || (
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            )}
          </OnboardingIcon>
          <OnboardingTitle>Get Started with {appName}</OnboardingTitle>
        </OnboardingHeader>
        
        <OnboardingDescription>{description}</OnboardingDescription>
        
        {showProgress && steps.length > 0 && (
          <ProgressBar>
            <ProgressFill progress={progress} color={color} />
          </ProgressBar>
        )}
        
        {steps.length > 0 && (
          <StepsList>
            {steps.map((step, index) => (
              <StepItem 
                key={step.id} 
                completed={step.completed}
                onClick={step.onClick}
                color={color}
                colorRgb={colorRgb}
              >
                <StepNumber completed={step.completed} color={color} colorRgb={colorRgb}>
                  {step.completed ? '✓' : index + 1}
                </StepNumber>
                <StepContent>
                  <StepTitle completed={step.completed}>{step.title}</StepTitle>
                  {step.description && (
                    <StepDescription>{step.description}</StepDescription>
                  )}
                </StepContent>
                {step.onClick && !step.completed && (
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="rgba(255,255,255,0.4)" 
                    strokeWidth="2"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                )}
              </StepItem>
            ))}
          </StepsList>
        )}
        
        {actionText && onAction && (
          <ActionButton onClick={onAction} color={color}>
            {actionText}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </ActionButton>
        )}
      </OnboardingCard>
    </OnboardingContainer>
  );
};

export default AppOnboarding;

